import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { 
  Gift, Copy, Share2, Users, CheckCircle, Mail, 
  DollarSign, Link as LinkIcon, Send
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * InviteAndEarn Component
 * 
 * Dashboard section for referral program - invite friends and earn credits.
 */
const InviteAndEarn = ({ api }) => {
  const [referralCode, setReferralCode] = useState('');
  const [referralLink, setReferralLink] = useState('');
  const [stats, setStats] = useState({
    total_invites: 0,
    successful_referrals: 0,
    total_credits_earned: 0,
    current_balance: 0
  });
  const [inviteEmail, setInviteEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchReferralData();
  }, []);

  const fetchReferralData = async () => {
    try {
      // Fetch referral code
      const codeResponse = await api.get('/users/referral-code');
      setReferralCode(codeResponse.data.referral_code);
      setReferralLink(codeResponse.data.referral_link);
      
      // Fetch stats
      const statsResponse = await api.get('/users/referral-stats');
      setStats(statsResponse.data);
    } catch (error) {
      console.error('Error fetching referral data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    toast.success('Link copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join HireHub Australia',
          text: 'Sign up with my link and get $10 credit for your first booking!',
          url: referralLink
        });
      } catch (error) {
        if (error.name !== 'AbortError') {
          handleCopyLink();
        }
      }
    } else {
      handleCopyLink();
    }
  };

  const handleSendInvite = async (e) => {
    e.preventDefault();
    
    if (!inviteEmail) {
      toast.error('Please enter an email address');
      return;
    }

    setSending(true);
    try {
      await api.post('/users/referral/send-invite', { email: inviteEmail });
      toast.success('Invite sent successfully!');
      setInviteEmail('');
      fetchReferralData(); // Refresh stats
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invite');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <Card className="border-slate-100">
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-slate-200 border-t-[#004B8D]"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-slate-100 overflow-hidden" data-testid="invite-and-earn-section">
      {/* Header with gradient */}
      <div className="bg-gradient-to-r from-[#004B8D] to-[#00A3BF] p-5 text-white">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
            <Gift className="w-6 h-6" />
          </div>
          <div>
            <h3 className="font-heading font-bold text-lg">Invite & Earn</h3>
            <p className="text-white/80 text-sm">Earn $10 for every friend who books</p>
          </div>
        </div>
      </div>
      
      <CardContent className="p-5 space-y-6">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-slate-50 rounded-xl p-3 text-center">
            <Users className="w-5 h-5 text-blue-500 mx-auto mb-1" />
            <p className="text-2xl font-bold text-slate-900">{stats.total_invites}</p>
            <p className="text-xs text-slate-500">Invites Sent</p>
          </div>
          <div className="bg-slate-50 rounded-xl p-3 text-center">
            <CheckCircle className="w-5 h-5 text-green-500 mx-auto mb-1" />
            <p className="text-2xl font-bold text-slate-900">{stats.successful_referrals}</p>
            <p className="text-xs text-slate-500">Successful</p>
          </div>
          <div className="bg-slate-50 rounded-xl p-3 text-center">
            <DollarSign className="w-5 h-5 text-amber-500 mx-auto mb-1" />
            <p className="text-2xl font-bold text-slate-900">${stats.total_credits_earned}</p>
            <p className="text-xs text-slate-500">Earned</p>
          </div>
          <div className="bg-slate-50 rounded-xl p-3 text-center">
            <DollarSign className="w-5 h-5 text-purple-500 mx-auto mb-1" />
            <p className="text-2xl font-bold text-slate-900">${stats.current_balance}</p>
            <p className="text-xs text-slate-500">Balance</p>
          </div>
        </div>

        {/* Referral Link */}
        <div>
          <Label className="text-sm font-medium text-slate-700 mb-2 block">
            Your Referral Link
          </Label>
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input 
                value={referralLink}
                readOnly
                className="pl-10 pr-4 bg-slate-50 text-sm"
                data-testid="referral-link-input"
              />
            </div>
            <Button 
              variant="outline" 
              size="icon"
              onClick={handleCopyLink}
              className={copied ? 'bg-green-50 border-green-200' : ''}
              data-testid="copy-link-btn"
            >
              {copied ? (
                <CheckCircle className="w-4 h-4 text-green-600" />
              ) : (
                <Copy className="w-4 h-4" />
              )}
            </Button>
            <Button 
              variant="outline" 
              size="icon"
              onClick={handleShare}
            >
              <Share2 className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-xs text-slate-500 mt-2">
            Code: <span className="font-mono font-semibold">{referralCode}</span>
          </p>
        </div>

        {/* Send Invite by Email */}
        <div className="pt-4 border-t border-slate-100">
          <Label className="text-sm font-medium text-slate-700 mb-2 block">
            Invite by Email
          </Label>
          <form onSubmit={handleSendInvite} className="flex gap-2">
            <div className="flex-1 relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input 
                type="email"
                placeholder="friend@example.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                className="pl-10"
                data-testid="invite-email-input"
              />
            </div>
            <Button 
              type="submit"
              disabled={sending}
              className="bg-[#004B8D] hover:bg-[#003865]"
              data-testid="send-invite-btn"
            >
              {sending ? (
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send
                </>
              )}
            </Button>
          </form>
        </div>

        {/* How it works */}
        <div className="bg-blue-50 rounded-xl p-4">
          <h4 className="font-semibold text-slate-900 mb-2 text-sm">How it works</h4>
          <ol className="space-y-1.5 text-xs text-slate-600">
            <li className="flex items-start gap-2">
              <span className="w-5 h-5 bg-[#004B8D] text-white rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-bold">1</span>
              Share your link with friends
            </li>
            <li className="flex items-start gap-2">
              <span className="w-5 h-5 bg-[#004B8D] text-white rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-bold">2</span>
              They sign up and complete their first booking
            </li>
            <li className="flex items-start gap-2">
              <span className="w-5 h-5 bg-[#004B8D] text-white rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-bold">3</span>
              You both get $10 credit!
            </li>
          </ol>
        </div>
      </CardContent>
    </Card>
  );
};

export default InviteAndEarn;
